import type { ReactNode } from "react";
import { defineField, defineType } from "sanity";

export const educationalContentType = defineType({
  type: "document",
  name: "educationalContent",
  title: "Educational Content",
  fields: [
    defineField({
      name: "language",
      type: "string",
      hidden: true,
      validation: (Rule) => Rule.required()
    }),
    defineField({
      name: "title",
      type: "string",
      title: "Title",
      validation: (Rule) => Rule.required()
    }),
    defineField({
      name: "taskSlug",
      type: "string",
      title: "Task Slug"
    }),
    defineField({
      name: "type",
      type: "string",
      title: "Type",
      options: {
        list: ["article", "video", "typeform", "coaching", "other"]
      },
      initialValue: "other",
      validation: (Rule) => Rule.required()
    }),
    defineField({
      name: "description",
      type: "string",
      title: "Description",
      validation: (Rule) =>
        Rule.custom((value, context) => {
          const requiredTypes = ["article", "video"];
          if (
            requiredTypes.includes(String(context.document?.type)) &&
            !value
          ) {
            return "Image is required";
          }
          return true;
        })
    }),
    defineField({
      name: "typeformId",
      type: "string",
      title: "Typeform ID",
      hidden: ({ parent }) => parent?.type !== "typeform",
      validation: (Rule) =>
        Rule.custom((value, context) => {
          if (context.document?.type === "types" && !value) {
            return "Required";
          }
          return true;
        })
    }),
    defineField({
      name: "url",
      type: "string",
      title: "URL for article or coaching",
      hidden: ({ parent }) =>
        parent?.type !== "article" && parent?.type !== "coaching"
    }),
    defineField({
      name: "sanctuaryHealthVideo",
      type: "reference",
      to: [{ type: "sanctuaryHealthVideo" }],
      title: "Sanctuary Health Video",
      hidden: ({ parent }) => parent?.type !== "video",
      validation: (Rule) =>
        Rule.custom((value, context) => {
          if (context.document?.type === "video" && !value) {
            return "Sanctuary Health Video ID is required for video type";
          }
          return true;
        })
    }),
    defineField({
      name: "image",
      type: "image",
      title: "Image",
      validation: (Rule) =>
        Rule.custom((value, context) => {
          const requiredTypes = ["article", "video"];
          if (
            requiredTypes.includes(String(context.document?.type)) &&
            !value
          ) {
            return "Image is required";
          }
          return true;
        })
    }),
    defineField({
      name: "linkedPrograms",
      type: "array",
      title: "Linked Programs",
      of: [{ type: "reference", to: [{ type: "programContent" }] }]
    }),
    defineField({
      name: "categories",
      type: "array",
      title: "Categories",
      of: [{ type: "reference", to: [{ type: "categoryContent" }] }]
    }),
    defineField({
      name: "hubspotBlogId",
      type: "string",
      hidden: true,
      description:
        "HubSpot blog post ID — set by automation, used for dedup and EN/ES pairing."
    }),
    defineField({
      name: "translatedFrom",
      type: "reference",
      to: [{ type: "educationalContent" }],
      hidden: ({ parent }) => parent?.language === "en",
      description: "Link to the source-language doc (set on ES docs only)."
    }),
    defineField({
      name: "hidden",
      type: "boolean",
      hidden: true,
      initialValue: false,
      description:
        "Soft-delete flag set by automation when the source HubSpot post is unpublished."
    })
  ],

  preview: {
    select: {
      title: "title",
      description: "description",
      type: "type",
      image: "image",
      slug: "taskSlug"
    },
    prepare(selection) {
      const {
        title,
        type,
        image,
        description = "",
        slug = ""
      } = selection as {
        title?: string;
        type?: string;
        image?: ReactNode;
        description?: string;
        slug?: string;
      };
      return {
        title: `${title ?? ""} [${type?.toUpperCase() ?? ""}]`,
        subtitle: `${slug ? `[.${slug}]` : ""} ${description}`,
        media: image
      };
    }
  }
});
